import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Loader2, RefreshCw } from "lucide-react";

type Row = { link_key: string; path: string; section: string | null; created_at: string };

const RANGES = [
  { days: 7, label: "7 dní" },
  { days: 30, label: "30 dní" },
  { days: 90, label: "90 dní" },
] as const;

/** Superadmin overview of which footer links people actually click. */
export function FooterClicksTab() {
  const { t } = useTranslation();
  const [days, setDays] = useState<number>(30);
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);

  const load = async (rangeDays: number) => {
    setLoading(true);
    const since = new Date(Date.now() - rangeDays * 24 * 60 * 60 * 1000).toISOString();
    const { data } = await supabase
      .from("footer_link_clicks")
      .select("link_key, path, section, created_at")
      .gte("created_at", since)
      .order("created_at", { ascending: false })
      .limit(5000);
    setRows((data ?? []) as Row[]);
    setLoading(false);
  };

  useEffect(() => {
    void load(days);
  }, [days]);

  const totals = new Map<string, { path: string; section: string | null; count: number }>();
  for (const r of rows) {
    const cur = totals.get(r.link_key);
    if (cur) cur.count += 1;
    else totals.set(r.link_key, { path: r.path, section: r.section, count: 1 });
  }
  const sorted = [...totals.entries()].sort((a, b) => b[1].count - a[1].count);
  const max = sorted[0]?.[1].count ?? 1;

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between gap-3 space-y-0">
        <CardTitle className="text-lg">Prokliky v patičce</CardTitle>
        <div className="flex items-center gap-2">
          {RANGES.map((r) => (
            <Button
              key={r.days}
              size="sm"
              variant={days === r.days ? "default" : "outline"}
              onClick={() => setDays(r.days)}
            >
              {r.label}
            </Button>
          ))}
          <Button size="sm" variant="ghost" onClick={() => void load(days)} aria-label="Obnovit">
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center gap-2 text-muted-foreground text-sm py-6">
            <Loader2 className="h-4 w-4 animate-spin" /> Načítám…
          </div>
        ) : sorted.length === 0 ? (
          <p className="text-sm text-muted-foreground py-6">
            Za zvolené období zatím nikdo na odkaz v patičce neklikl.
          </p>
        ) : (
          <>
            <p className="text-sm text-muted-foreground mb-4">
              Celkem <strong>{rows.length}</strong> prokliků za posledních {days} dní.
            </p>
            <ul className="space-y-3">
              {sorted.map(([key, info]) => (
                <li key={key}>
                  <div className="flex items-baseline justify-between gap-3 text-sm">
                    <span className="font-medium">
                      {t(`footer.links.${key}`, { defaultValue: key })}{" "}
                      <span className="text-muted-foreground font-normal">{info.path}</span>
                    </span>
                    <span className="tabular-nums font-semibold">{info.count}</span>
                  </div>
                  <div className="mt-1 h-1.5 rounded-full bg-muted overflow-hidden">
                    <div
                      className="h-full rounded-full bg-primary"
                      style={{ width: `${Math.max(4, (info.count / max) * 100)}%` }}
                    />
                  </div>
                </li>
              ))}
            </ul>
          </>
        )}
      </CardContent>
    </Card>
  );
}
